/**
 * Unified variant system for action cards and entity displays.
 *
 * Standardises the visual treatment (background, border, text) across all
 * proposal/action card components.
 *
 * These are tinted surfaces, so they are spelled with the `utility-*` family —
 * the only status family that fully auto-inverts (VISUAL_STYLE_GUIDE.md
 * § Dark mode). The raw `error-*`/`warning-*` scales these used to carry are
 * fixed light values in BOTH themes, which is why every entry needed a
 * hand-written `dark:` partner; `utility-*` remaps under `.dark-mode`, so the
 * `dark:` halves are gone and adding one back would double-flip.
 *
 * Pick the map that matches your component's role:
 *
 *   VARIANT_CARD       inner entity cards (bg-secondary default)
 *   VARIANT_SHELL      outer full-card wrappers (bg-primary default)
 *   VARIANT_BORDER     border-only override (for shells that manage their own bg)
 *   VARIANT_HEADER_BG  header / section tinting
 *   VARIANT_TEXT        label / badge text color
 */

/** The three visual variants used across action and entity cards. */
export type ActionVariant = 'default' | 'destructive' | 'warning';

/** Full bg + border for inner entity cards (bg-secondary default). */
export const VARIANT_CARD: Record<ActionVariant, string> = {
  default: 'bg-bg-secondary border-border-secondary',
  destructive: 'bg-utility-error-50 border-utility-error-200',
  warning: 'bg-utility-warning-50 border-utility-warning-200',
};

/** Full bg + border for outer shell cards (bg-primary default). */
export const VARIANT_SHELL: Record<ActionVariant, string> = {
  default: 'bg-bg-primary border-border-secondary',
  destructive: 'bg-utility-error-50 border-utility-error-200',
  warning: 'bg-utility-warning-50 border-utility-warning-200',
};

/** Border-only overrides for components that manage their own background. */
export const VARIANT_BORDER: Record<ActionVariant, string> = {
  default: '',
  destructive: 'border-utility-error-200',
  warning: 'border-utility-warning-200',
};

/** Header / section background tinting. */
export const VARIANT_HEADER_BG: Record<ActionVariant, string> = {
  default: '',
  destructive: 'bg-utility-error-50',
  warning: 'bg-utility-warning-50',
};

/** Text color for variant-specific labels and badges. */
export const VARIANT_TEXT: Record<ActionVariant, string> = {
  default: 'text-fg-secondary',
  destructive: 'text-utility-error-600',
  warning: 'text-utility-warning-600',
};
